import React, {useContext, useEffect, useRef, useState} from 'react';
import {Button, Col, OverlayTrigger, Row, Spinner, Tooltip} from "react-bootstrap";
import {SiMicrosoftexcel} from "react-icons/si";
import {store} from "../../Store/store";
import BootstrapTable from "react-bootstrap-table-next";
import filterFactory, {Comparator, customFilter, dateFilter, FILTER_TYPES, numberFilter, selectFilter, textFilter} from "react-bootstrap-table2-filter";
import paginationFactory from "react-bootstrap-table2-paginator";
import MultiSelectFilter from "../MultiSelectFilter/MultiSelectFilter";
import {RiFilterOffFill} from "react-icons/ri";
import {FaColumns} from "react-icons/fa";
import './Table.css';
import {Multiselect} from "multiselect-react-dropdown";
import {CSVLink} from "react-csv";
import SystemCycleMultiSelect from "../SystemCycleMultiSelect/SystemCycleMultiSelect";
import {hasAdminRoles} from "../../General/SharedMethods";

/*  Props:
    data: [Array] Table Data
    columns: [Array] Table Columns
    pageSize: [Integer] Entries per page
    rowStyle: [Function] Row Styling
    cellEdit: [Function] Cell Editing options
    defaultSort: [Object] Default sorting settings
    resetFilters: [Function] Function called when clicked the Clear Filters button
    exportCsv: [Boolean] Add a CSV Export Button
    csvFileName: [String] Name of the file generated by the Export Button
 */
export default function Table({
                                  data,
                                  columns,
                                  enableColumnHider,
                                  pageSize,
                                  resetFilters,
                                  handleTableColumnFilter,
                                  setTableFilteredData,
                                  exportCsv,
                                  csvFileName,
                                  systemCycleCallBack,
                                  setSelectedSystemStates,
                                  isRequestInProgress,
                                  keyField,
                                  defaultSort,
                                  rowStyle,
                                  cellEdit,
                                  disablePagination,
                                  showClearFiltersButton,
                                  additionalToolbarComponent,
                                  isHeadersSticky
                              }) {
    const globalState = useContext(store);
    const [tableColumns, setTableColumns] = useState(columns);
    const [excelFilteredData, setExcelFilteredData] = useState(data);
    const [shownColumns, setShownColumns] = useState(columns.map(col => {
        return {value: col.dataField, label: col.text, isDefault: !!col.isDefault}
    }));
    const exportToExcelButtonRef = useRef(null);
    const clearFiltersButtonRef = useRef(null);
    const resetHiddenColumnsButtonRef = useRef(null);
    const [previousFilterObject, setPreviousFilterObject] = useState({});
    const enableTableColumnHider = enableColumnHider !== undefined ? enableColumnHider : true;


    const paginationOptions = {
        paginationSize: 20,
        pageStartIndex: 1,
        hideSizePerPage: true,
        hidePageListOnlyOnePage: false,
        sizePerPageList: [{
            text: pageSize || '100', value: pageSize || 100
        }]
    }

    const clearFiltersButton = <Button variant="info" onClick={resetFilters}
                                       style={{position: 'absolute', width: '15vh', top: 5, right: 40}}>Clear
        Filters</Button>

    const initColumns = () => {
        setTableColumns(columns.filter(col => {
            return (shownColumns.map(function (e) {
                return e.value;
            }).indexOf(col.dataField) !== -1) && (!col.adminColumn || (hasAdminRoles(globalState.state.userRole) && col.adminColumn))
        }))
    }

    const resetHiddenColumnsSelection = () => {
        setShownColumns(columns.map(col => {
            return {value: col.dataField, label: col.text, isDefault: !!col.isDefault}
        }));
    }

    const afterFilter = (newResult, newFiltersObject) => {
        if (JSON.stringify(excelFilteredData) !== JSON.stringify(newResult)) {
            setExcelFilteredData(newResult);
        }
        if (handleTableColumnFilter && setTableFilteredData) {
            const newFiltersKeys = Object.keys(newFiltersObject);
            const previousFiltersKeys = Object.keys(previousFilterObject);
            let isObjectsSimilar = true;
            if (newFiltersKeys.length !== previousFiltersKeys.length) {
                isObjectsSimilar = false;
            }
            newFiltersKeys.forEach(key => {
                if (!(previousFilterObject.hasOwnProperty(key))) {
                    isObjectsSimilar = false;
                } else {
                    switch (newFiltersObject[key].filterType) {
                        case 'TEXT': {
                            if (newFiltersObject[key].filterVal !== previousFilterObject[key].filterVal) {
                                isObjectsSimilar = false;
                            }
                            break;
                        }
                        case 'SELECT': {
                            if (newFiltersObject[key].filterVal !== previousFilterObject[key].filterVal) {
                                isObjectsSimilar = false;
                            }
                            break;
                        }
                        case 'MULTISELECT': {
                            if (JSON.stringify(newFiltersObject[key].filterVal) !== (JSON.stringify(previousFilterObject[key].filterVal))) {
                                isObjectsSimilar = false;
                            }
                            break;
                        }
                    }
                }
            })
            if (!isObjectsSimilar) {
                handleTableColumnFilter(newResult);
                setTableFilteredData(newResult);
                setPreviousFilterObject(newFiltersObject);
            }

        }
    }

    useEffect(() => {
        setExcelFilteredData(data);
    }, [data])

    useEffect(() => {
        initColumns();
    }, [])

    useEffect(() => {
        initColumns();
    }, [shownColumns, hasAdminRoles(globalState.state.userRole)])

    return (<>
        {exportCsv && <div>
            <Row>
                <Col style={{display: 'inline-flex', alignItems: 'center'}}>
                    <OverlayTrigger placement="top" overlay={<Tooltip>Export to Excel</Tooltip>}>
                        <CSVLink
                            filename={csvFileName.endsWith(".csv") ? csvFileName : `${csvFileName}.csv`}
                            data={excelFilteredData}
                            ref={exportToExcelButtonRef}
                            className="btn export-to-excel-button"
                            target="_blank">
                            <SiMicrosoftexcel style={{color: 'white'}}/>
                        </CSVLink>
                    </OverlayTrigger>
                    <OverlayTrigger placement="top" overlay={<Tooltip>Clear Filters</Tooltip>}>
                        <Button ref={clearFiltersButtonRef} onClick={resetFilters}
                                className={'clear-filters-button'}><RiFilterOffFill/></Button>
                    </OverlayTrigger>
                    {enableTableColumnHider &&
                        <>
                            <OverlayTrigger placement="top"
                                            overlay={<Tooltip>Reset Hidden Columns</Tooltip>}>
                                <Button ref={resetHiddenColumnsButtonRef}
                                        onClick={resetHiddenColumnsSelection}
                                        className={'clear-hidden-columns-button'}><FaColumns/>
                                </Button>

                            </OverlayTrigger>
                            <div style={{width: '180px'}}>
                                <ColumnHider columns={columns} shownColumns={shownColumns}
                                             setShownColumns={setShownColumns}/>
                            </div>
                            {systemCycleCallBack && setSelectedSystemStates ?
                                <div style={{marginLeft: '5px'}}>
                                    <SystemCycleMultiSelect isRequestInProgress={isRequestInProgress} systemCycleCallBack={systemCycleCallBack}
                                                            setSelectedSystemStates={setSelectedSystemStates}/>
                                </div>
                                :
                                null
                            }
                        </>
                    }
                </Col>
            </Row>
            {isRequestInProgress ?
                <Spinner animation="border" variant="primary"/> :
                <div className={isHeadersSticky ? "sticky-header-table": ""}>
                <BootstrapTable
                    keyField={keyField ? keyField : 'id'}
                    data={data}
                    columns={tableColumns}
                    sort={defaultSort ? defaultSort : {dataField: 'updateDate', order: 'desc'}}
                    filter={filterFactory({afterFilter})}
                    rowStyle={rowStyle}
                    cellEdit={cellEdit}
                    pagination={!disablePagination && data.length !== 0 ? paginationFactory(paginationOptions) : null}
                    bootstrap4
                    condensed
                    striped
                    hover
                />
            </div>}
        </div>}
        {!exportCsv && <div>
            <Row>
                {showClearFiltersButton && <Col xs={1}>
                    <Button variant="info" onClick={resetFilters} style={{width: '15vh', margin: '5px'}}>Clear
                        Filters</Button>
                </Col>}
                {additionalToolbarComponent}
            </Row>
            <div className={isHeadersSticky ? "sticky-header-table": ""}>
            <BootstrapTable
                keyField={keyField ? keyField : 'id'}
                data={data}
                columns={tableColumns}
                sort={defaultSort ? defaultSort : {dataField: 'updateDate', order: 'desc'}}
                filter={filterFactory({afterFilter})}
                rowStyle={rowStyle}
                cellEdit={cellEdit}
                pagination={!disablePagination && data.length !== 0 ? paginationFactory(paginationOptions) : null}
                bootstrap4
                condensed
                striped
                hover
            />
            </div>
        </div>}
    </>)
}

function ColumnHider({
                         columns,
                         shownColumns,
                         setShownColumns
                     }) {
    const multiselectOptions = columns.filter(col => {
        return !col.isDefault
    }).map(col => {
        return {value: col.dataField, label: col.text}
    });
    const numOfHideableColumns = shownColumns?.filter(col => {
        return !col.isDefault
    }).length;

    return (<Multiselect
        options={multiselectOptions}
        selectedValues={shownColumns}
        style={{chips: {visibility: 'hidden', position: 'absolute', zIndex: -1000}}}
        showCheckbox
        placeholder={numOfHideableColumns === multiselectOptions.length ? 'Hide Columns' : `${multiselectOptions.length - numOfHideableColumns} Hidden Columns`}
        closeOnSelect={false}
        hidePlaceholder={false}
        avoidHighlightFirstOption={true}
        onSelect={(selectedList, selectedOption) => {
            let options = shownColumns;
            options = options.concat([selectedOption]);
            setShownColumns(options);
        }}
        onRemove={(selectedList, selectedOption) => {
            let options = shownColumns;
            options = options.filter(currOption => {
                return currOption.value !== selectedOption.value
            });
            setShownColumns(options);
        }}
        displayValue="label"/>)
}

export const likeCompMultiSelectFilter = customFilter({
    type: FILTER_TYPES.MULTISELECT, comparator: Comparator.LIKE
})

export const eqCompMultiSelectFilter = customFilter({
    type: FILTER_TYPES.MULTISELECT, comparator: Comparator.EQ
})

export const buildSelectFilter = (filtersRefs, options, defaultValue) => {
    return selectFilter({
        placeholder: 'All Values',
        options: options,
        defaultValue: defaultValue ? defaultValue : null,
        style: {height: '22px', fontSize: 10, padding: 0},
        getFilter: (filter) => {
            filtersRefs?.current.push(filter);
        }
    })
}

export const buildMultiSelectFilterRenderer = (ref, options, selectedValues) => {
    return (onFilter, column) => {
        return <MultiSelectFilter ref={ref} selectedValues={selectedValues} onFilter={onFilter} column={column}
                                  options={options}/>;
    }
}


export const tableDateFilter = (filtersRefs) => {
    return dateFilter({
        getFilter: (filter) => {
            filtersRefs.current.push(() => filter('dd/mm/yyyy'));
        },
        comparators: [Comparator.GT, Comparator.GE, Comparator.LT, Comparator.LE],
        dateStyle: {height: '22px', width: '115px', fontSize: 12},
        comparatorStyle: {
            height: '22px',
            width: '42px',
            padding: 0,
            fontSize: 12,
            lineHeight: 2
        },
    })

}

export const tableNumberFilter = (filtersRefs) => {
    return numberFilter({
        style: {height: '22px'},
        getFilter: (filter) => {
            filtersRefs.current.push(filter);
        },
        placeholder: ' ',
        numberStyle: {padding: 0, height: '22px', width: 'auto', maxWidth: '70px', fontSize: 12},
        comparatorStyle: {
            height: '22px',
            width: '42px',
            padding: 0,
            fontSize: 12,
            lineHeight: 2
        },
    })
}

export const tableTextFilter = (filtersRefs) => {
    return textFilter({
        style: {height: '22px', fontSize: 10}, getFilter: (filter) => {
            filtersRefs.current.push(filter);
        }
    })
}

export const numberFormatter = (cell, row) => {
    return cell ? Number(cell).toLocaleString() : '';
}
